import type { CSSProperties, FC, ReactNode } from 'react';
import {
  MetaLabelProvider,
  MetaLabels,
} from 'src/components/data-display/meta-label';
import { StatusDisplayProvider } from 'src/components/data-display/status-display';
import { VerticalDivider } from 'src/components/dividers';
import { Heading } from 'src/components/text/heading';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useMeasure } from 'src/hooks/use-measure';

type PageHeaderProps = {
  title: ReactNode | string;
  metaLabels?: ReactNode | string;
  description?: ReactNode | string;
  /** Accepts only <a href="./?path=/docs/components-data-display-status-display--docs">Status Display</a> component. */
  status?: ReactNode;
  /** No more than 2 actions. If you want to insert more actions/buttons, consider using <a href="./?path=/docs/components-buttons-actions-split-actions--docs">Split Actions</a> component to group them. */
  actions?: ReactNode | [ReactNode] | [ReactNode, ReactNode];
  /** The `backLink` object resulting from `useReferrer()` set in the parent component (usually a Page) is passed to this prop in order to show the back link. */
  backLink?: ReactNode;
  'data-testid'?: string;
};

export const PageHeader: FC<PageHeaderProps> = (props) => {
  const {
    title,
    metaLabels,
    description,
    status,
    actions,
    backLink,
    ['data-testid']: testId = 'page-header',
  } = props;
  const [statusRef, statusBounds] = useMeasure();
  const [actionsRef, actionsBounds] = useMeasure();
  const dividerHeight = Math.min(statusBounds.height, actionsBounds.height);
  const { minDesktop } = useBreakpoints();

  return (
    <div
      className={`
        grid items-center gap-sm
        legacy:gap-[0.6rem]
      `}
      data-testid={testId}
    >
      {!!backLink && backLink}

      <div
        className={`
          grid auto-cols-[fit-content()] grid-flow-row items-center
          justify-between gap-md
          lg:grid-flow-col lg:gap-xl
          legacy:gap-[1rem]
          legacy:lg:gap-[2rem]
        `}
      >
        <div className="grid items-center">
          <Heading mode="headline" size="lg" data-testid={`${testId}-title`}>
            {title}
          </Heading>
          <div
            className={`
              grid items-center gap-md
              legacy:gap-[1rem]
            `}
          >
            {!!metaLabels && (
              <MetaLabelProvider value="page">
                <MetaLabels>{metaLabels}</MetaLabels>
              </MetaLabelProvider>
            )}

            {!!description && (
              <div data-testid={`${testId}-description`}>{description}</div>
            )}
          </div>
        </div>

        {(!!status || !!actions) && (
          <div
            className={`
              grid grid-flow-row items-center justify-start gap-md
              lg:grid-flow-col lg:gap-xl
              legacy:gap-[1rem]
              legacy:lg:gap-[2rem]
            `}
          >
            {!!status && (
              <StatusDisplayProvider value="page">
                <div
                  className="grid items-center"
                  ref={statusRef}
                  data-testid={`${testId}-status`}
                >
                  {status}
                </div>
              </StatusDisplayProvider>
            )}

            {minDesktop && !!status && !!actions && (
              <VerticalDivider
                style={
                  {
                    '--divider-height': `${dividerHeight}px`,
                  } as CSSProperties
                }
                className="h-(--divider-height)"
                data-testid={`${testId}-divider`}
              />
            )}

            {!!actions && (
              <div
                className={`
                  grid grid-cols-[auto_auto] items-center gap-md
                  legacy:gap-[1rem]
                `}
                ref={actionsRef}
                data-testid={`${testId}-actions`}
              >
                {actions}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
